import {
  type PlotPanelState,
  type PlotSeries,
} from "@/shared/state/visualization";
import { DEFAULT_PLOT_PANEL_CONFIG } from "@/shared/state/visualization/context";
import { LegendPosition } from "@/shared/state/visualization/schema";
import { PlotYAxis } from "@/shared/state/visualization/schema/v1";

import { type PanelSettingsSidebarProps } from "../../sidebarContext";
import { BasePanelSettings } from "../BasePanelSettings";
import { SettingsSection } from "../SettingsSection";
import styles from "../SettingsSection.module.css";
import { Spotlight } from "../Spotlight";

import { AddSeries } from "./AddSeries";
import { AxisLabelInput } from "./AxisLabelInput";
import { AxisRangeInput } from "./AxisRangeInput";
import { ConfigItem } from "./ConfigItem";
import { PlotSeriesConfig } from "./PlotSeriesConfig";
import { SelectLegendPosition } from "./SelectLegendPosition";
import { SelectTimeBoundsSource } from "./SelectTimeBoundsSource";

export interface PlotPanelSettingsSidebarProps
  extends PanelSettingsSidebarProps {
  field?: "Series";
}

interface PlotPanelSettingsProps extends PlotPanelSettingsSidebarProps {
  state: PlotPanelState;
}

export function PlotPanelSettings({
  field,
  state,
  spotlightKey,
}: PlotPanelSettingsProps) {
  const extraYAxes = state.config?.extraYAxes ?? {};
  return (
    <BasePanelSettings state={state}>
      <SettingsSection title="Axes">
        <ConfigItem label="Time Range">
          <SelectTimeBoundsSource
            panelId={state.id}
            timeBoundsSource={
              state.config?.timeBoundsSource ||
              DEFAULT_PLOT_PANEL_CONFIG.timeBoundsSource
            }
          />
        </ConfigItem>
        <ConfigItem label="X-Axis">
          <AxisLabelInput
            axis="x"
            currentAxisConfig={state.config?.xAxis}
            panelId={state.id}
          />
        </ConfigItem>
        <ConfigItem label="Y-Axis">
          <AxisLabelInput
            axis="y"
            currentAxisConfig={state.config?.yAxis}
            panelId={state.id}
          />
          <AxisRangeInput
            currentBounds={state.config?.yAxis?.bounds}
            panelId={state.id}
            axis="y"
            state={state}
          />
        </ConfigItem>
        {Object.keys(extraYAxes).length > 0 &&
          Object.keys(extraYAxes)
            .sort()
            .map((k) => {
              const key = k as PlotYAxis;
              const label =
                key === "y"
                  ? "Default Y-Axis (Left)"
                  : `Y-Axis ${key.substring(1, key.length - 1)} (${key.endsWith("r") ? "Right" : "Left"})`;

              return (
                <ConfigItem label={label} key={key}>
                  <AxisLabelInput
                    axis={key}
                    currentAxisConfig={state.config?.extraYAxes?.[key]}
                    panelId={state.id}
                  />
                  <AxisRangeInput
                    axis={key}
                    currentBounds={state.config?.extraYAxes?.[key]?.bounds}
                    panelId={state.id}
                    state={state}
                  />
                </ConfigItem>
              );
            })}
      </SettingsSection>
      <SettingsSection title="Legend">
        <ConfigItem label="Position">
          <SelectLegendPosition
            panelId={state.id}
            legendPosition={
              state.config?.legendPosition ??
              DEFAULT_PLOT_PANEL_CONFIG.legendPosition ??
              LegendPosition.TopLeft
            }
          />
        </ConfigItem>
      </SettingsSection>
      <SettingsSection title="Series">
        <div className={styles.settings}>
          {state.data.map((plotSeries: PlotSeries, idx) => (
            <PlotSeriesConfig
              key={plotSeries.id}
              order={idx + 1}
              panelId={state.id}
              plotSeries={plotSeries}
              allowedYAxes={state.data.length}
            />
          ))}
        </div>
        <Spotlight
          className={styles.settingContainer}
          spotlightKey={field === "Series" ? spotlightKey : undefined}
        >
          <AddSeries plotPanel={state} />
        </Spotlight>
      </SettingsSection>
    </BasePanelSettings>
  );
}
